import React from 'react';
import styled from 'styled-components';
import t from 'modules/i18n/intl';
import config from 'appconfig';
import { FormattedDate } from 'react-intl';
import ChevronUpIcon from '@atlaskit/icon/glyph/hipchat/chevron-up';
import ChevronDownIcon from '@atlaskit/icon/glyph/hipchat/chevron-down';
import AmountWithCurrency from 'components/common/AmountWithCurrency';
import Button from '@atlaskit/button';
import EditFilledIcon from '@atlaskit/icon/glyph/edit-filled';
import TrashIcon from '@atlaskit/icon/glyph/trash';
import { Dimensions } from 'components/visuals';

const Cell = styled.td<{ width: number }>`
  font-size: 14px;
  align-self: center;
  width: ${({ width }) => width}%;
  padding: ${Dimensions.units(1)}px 0;

  && span {
    line-height: unset;
  }
`;

const ChevronCellWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const getOrderStatus = (
  { ccpQuoteMigrationStatus, status, ccpQuoteId }: any,
  isPartner?: boolean,
) => {
  if (ccpQuoteMigrationStatus === 'inprogress') {
    return t('mac.orders.table.table-item.status.ccp-migration');
  }

  if (ccpQuoteMigrationStatus === 'completed' && status === 'DELETED') {
    if (isPartner) {
      return t('mac.orders.table.table-item.status.ppc-migration', { ccpQuoteId });
    }
    return t('mac.orders.table.table-item.status.migration', { ccpQuoteId });
  }

  if (status === 'REVIEW_REQUIRED') return 'UNDER REVIEW';
  if (status === 'QUOTED') return '';

  return 'N/A';
};

const isCartStatusEditable = (status: string) =>
  ['QUOTED', 'REVIEW_REQUIRED', 'DELETED'].indexOf(status) === -1;

type OrdersActionLinkType = {
  label: any;
  href?: string;
  disabled?: boolean;
  onClick?: () => void;
};

const getCCPMigrationActions = (rowItem: any, isPartner?: boolean) => {
  const { ccpQuoteTxaId, ccpQuoteUuid, ccpQuoteMigrationStatus, cartOrderDetails } = rowItem;
  const links: OrdersActionLinkType[] = [];
  if (
    ccpQuoteMigrationStatus !== 'inprogress' &&
    (cartOrderDetails.currency !== 'AUD' ||
      cartOrderDetails.partner?.organisationDetails?.payOnAccount)
  ) {
    if (isPartner) {
      links.push({
        label: t('mac.orders.table.table-item.action.pay'),
        href: `${config.ppcUrl}/account/${ccpQuoteTxaId}/quotes/${ccpQuoteUuid}`,
      });
    }

    links.push({
      label: t('mac.orders.table.table-item.action.pay'),
      href: `${config.adminHubUrl}/billing/${ccpQuoteTxaId}/quotes/${ccpQuoteUuid}`,
    });
  } else {
    links.push({
      label: t('mac.orders.table.table-item.action.pay'),
      disabled: true,
    });
  }

  return links;
};

const getQuotedCartActions = (rowItem: any, isPartner?: boolean) => {
  const { orderId, cloneable, ccpQuoteMigrationStatus, cartOrderDetails, uuid } = rowItem;
  const links: OrdersActionLinkType[] = [];
  if (isPartner) {
    links.push({
      label: t('mac.orders.table.table-item.action.clone'),
      // TODO: update when clone quote page is finished in scope of CHT-2817
      href: `/billing/cloneQuote/${orderId}`,
      disabled: !cloneable,
    });
  }

  const pdfLink = {
    label: 'PDF',
    // TODO: update link when pdf download is finished in scope of CHT-2816
    href: `/billing/pdf/${orderId}`,
  };

  const jsonLink = {
    label: 'JSON',
    // TODO: update link when json download is finished in scope of CHT-2816
    href: `/billing/JSON/${orderId}`,
  };

  const invoiceLink = {
    label: t('mac.orders.table.table-item.action.invoice'),
    // TODO: update link when invoice download is finished in scope of CHT-2816
    href: `/billing/proforma/${orderId}`,
  };

  links.push(pdfLink, jsonLink, invoiceLink);

  if (
    ccpQuoteMigrationStatus !== 'inprogress' &&
    (cartOrderDetails.currency !== 'AUD' ||
      cartOrderDetails.partner?.organisationDetails?.payOnAccount)
  ) {
    links.push({
      label: t('mac.orders.table.table-item.action.pay'),
      href: `/purchase/checkout/payment?uuid=${uuid}`,
    });
  } else {
    links.push({
      label: t('mac.orders.table.table-item.action.pay'),
      disabled: true,
    });
  }

  links.push({
    label: t('mac.orders.table.table-item.action.update-po'),
    // TODO: update link when update po action task is finished in scope of CHT-2787
    href: '#',
  });
  return links;
};

const getDeletedCartActions = (rowItem: any, isPartner?: boolean, urlOrderId?: string) => {
  const { status, orderId, cartOrderDetails, uuid, isEditableByCustomer } = rowItem;
  const links: OrdersActionLinkType[] = [];

  if (urlOrderId || (!!cartOrderDetails && orderId && isCartStatusEditable(status))) {
    links.push({
      label: <EditFilledIcon label={t('mac.orders.table.table-item.action.edit')} />,
      // TODO: update link when edit action task is finished
      href: `/purchase/checkout/review?purchaseMode=quote&uuid=${uuid}`,
      disabled: !isEditableByCustomer,
    });
  } else {
    links.push({
      label: <TrashIcon label={t('mac.orders.table.table-item.action.delete')} />,
      // TODO: update link when delete action task is finished in scope of CHT-2788
      href: '#',
    });
  }

  return links;
};

const getOrderActions = (
  rowItem: any,
  isPartner?: boolean,
  urlOrderId?: string,
): OrdersActionLinkType[] => {
  const { status, orderId, ccpQuoteMigrationStatus } = rowItem;

  if (ccpQuoteMigrationStatus === 'inprogress' || ccpQuoteMigrationStatus === 'completed') {
    return getCCPMigrationActions(rowItem, isPartner);
  }
  if (status === 'QUOTED') {
    return getQuotedCartActions(rowItem, isPartner);
  }
  if (status !== 'DELETED') {
    // TODO: update this rule with hash if user is not logged in when hash functionality is finished in scope of CHT-2617
    return getDeletedCartActions(rowItem, isPartner, urlOrderId);
  }
  return [
    {
      label: 'JSON',
      // TODO: update link when json download is finished in scope of CHT-2816
      href: `/billing/JSON/${orderId}`,
    },
  ];
};

export const OrderCollapsedRow = ({
  showContent,
  rowItem,
  isPartner,
  urlOrderId,
}: {
  showContent: boolean;
  rowItem: any;
  isPartner?: boolean;
  urlOrderId?: string;
}) => {
  const { cartOrderDetails, orderNumber } = rowItem;
  const createdDate = new Date(cartOrderDetails?.created);
  const links = getOrderActions(rowItem, isPartner, urlOrderId);

  return (
    <>
      <Cell width={15} key="orders-row-sen">
        <ChevronCellWrapper>
          {showContent ? <ChevronUpIcon label="" /> : <ChevronDownIcon label="" />}
          {orderNumber}
        </ChevronCellWrapper>
      </Cell>
      <Cell width={15} key="orders-row-created">
        <FormattedDate value={createdDate} year="numeric" month="short" day="2-digit" />
      </Cell>
      <Cell width={15} key="orders-row-total">
        <AmountWithCurrency
          amount={cartOrderDetails?.totalIncTax}
          currency={cartOrderDetails?.currency || 'USD'}
          weight="400"
        />
      </Cell>
      <Cell width={15} key="orders-row-status">
        {getOrderStatus(rowItem, isPartner)}
      </Cell>
      <Cell width={15} key="orders-row-actions">
        {links.map((link) => (
          <Button
            appearance="link"
            href={link.href}
            key={link.label}
            isDisabled={link.disabled}
            onClick={link.onClick}
          >
            {link.label}
          </Button>
        ))}
      </Cell>
    </>
  );
};
